import { useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useToast } from 'components/hooks/useToast';

import { OKR_TYPES, TOAST_STATUSES } from 'constants/ProjectConstants';
import { StringModifier, modifyString } from 'constants/copyHelpers';
import { sendOKRUnarchivedEvent } from 'constants/mixpanelHelper';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import { useUnarchiveOKRMutation } from './datahooks';

export const useUnarchiveOkrConfirmationModalLogic = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();

  const [okrDetails, setOkrDetails] = useState({ okrUuid: null, okrType: OKR_TYPES.OBJECTIVE });

  const isObjective = okrDetails?.okrType === OKR_TYPES.OBJECTIVE;

  const { mutate } = useUnarchiveOKRMutation(okrDetails?.okrUuid);

  const handleCloseConfirmationModal = () => {
    setOkrDetails({
      okrUuid: null,
      okrType: null,
    });
  };

  const handleOpenConfirmationModal = ({ okrUuid, okrType }) => {
    setOkrDetails({
      okrUuid,
      okrType,
    });
  };

  const handleUnarchiveOkr = () => {
    const archivedOKRQueryKey = [REACT_QUERY_KEYS.archivedOkrs];

    mutate(
      /* @ts-ignore */
      {},
      {
        onError: (err) => {
          toast({
            title:
              /* @ts-ignore */
              err.message ||
              `The ${
                isObjective
                  ? modifyString(objectivesName, StringModifier.SENTENCE_CASE)
                  : modifyString(keyResultsName, StringModifier.SENTENCE_CASE)
              } could not be deleted`,
            status: TOAST_STATUSES.ERROR,
            position: 'bottom-left',
          });
        },
        onSuccess: () => {
          queryClient.invalidateQueries(archivedOKRQueryKey);

          sendOKRUnarchivedEvent({
            eventProperties: {
              goalId: okrDetails?.okrUuid,
              type: isObjective ? OKR_TYPES.OBJECTIVE : OKR_TYPES.KEY_RESULT,
              source: 'Kebab Menu',
              page: 'Archives',
            },
          });

          toast({
            title: `The ${
              isObjective
                ? modifyString(objectivesName, StringModifier.SENTENCE_CASE)
                : modifyString(keyResultsName, StringModifier.SENTENCE_CASE)
            } has been un-archived successfully`,
            status: TOAST_STATUSES.SUCCESS,
            position: 'bottom-left',
          });

          handleCloseConfirmationModal();
        },
      }
    );
  };

  return {
    isConfirmationModalOpen: Boolean(okrDetails?.okrUuid),
    isUnarchiveObjectiveConfirmationModal: isObjective,
    handleOpenConfirmationModal,
    handleCloseConfirmationModal,
    handleUnarchiveOkr,
  };
};
